import { SustainabilityIconConditions } from '@aboutyou/legacy-shared/src/bapi/product/getSustainabilityIconConditions'
import { BadgeListProps } from '@aboutyou/legacy-shared/src/ui/BadgeList/types'
import React from 'react'
import styled, { css } from 'styled-components'
import { bordersColor, complementaryColor } from 'uiComponents/constants/colors'
import ProductImage from '../ProductImage/ProductImage'
import ProductImageWithBadges, {
  BadgesContainer,
} from '../ProductImage/ProductImageWithBadges'
import ProductTileContent, {
  CTAButton,
  CTAHeight,
  ColorContainer,
  ContentWrapper,
  ProductTileContentProps,
  Sizes,
} from './ProductTileContent'
import ProductTileHover, { HoverImage } from './ProductTileHover'

export const ProductTileSelector = styled.section<{
  href?: string
  hasCTA?: boolean
}>`
  cursor: pointer;
  display: block;
  overflow: hidden;
  position: relative;
  z-index: 0;
  background: ${complementaryColor};
  border: 1px solid transparent;
  padding: 8px 8px 12px;
  border-radius: 4px;
  height: fit-content;
  transition: border-color 250ms ease-in-out;
  ${BadgesContainer} {
    z-index: 1;
  }
  ${props =>
    props.hasCTA &&
    css`
      ${ContentWrapper}, ${BadgesContainer} {
        transition: transform 250ms ease-in-out;
        transition-delay: 100ms;
      }
    `}

  &:hover {
    border-color: ${bordersColor};
    ${Sizes} {
      opacity: 1;
      transform: none;
    }
    ${ColorContainer} {
      opacity: 0;
      transform: translateY(-5px);
    }
    ${HoverImage} {
      opacity: 1;
      transform: none;
      transition-delay: 150ms;
    }
    ${props =>
      props.hasCTA &&
      css`
        ${ContentWrapper}, ${BadgesContainer} {
          transform: translateY(-${CTAHeight}px);
          transition-delay: 0ms;
        }
        ${CTAButton} {
          opacity: 1;
          transition-delay: 200ms;
        }
      `}
  }
`

export interface ProductTileInfo extends ProductTileContentProps {
  href?: string
  primaryImageDefaultSource: string
  primaryImageTypeTitle: string
  secondaryImageDefaultSource?: string
  secondaryImageTypeTitle?: string
  primaryImageSrcSet: string
  secondaryImageSrcSet?: string
  sizes?: string
  primaryPadded: boolean
  secondaryPadded: boolean
  badgesList?: BadgeListProps
  wishlisted?: boolean
  sustainability?: SustainabilityIconConditions
  enableSustainabilityIcons: boolean
  enablePriceDisplayOverhaul: boolean
  isAggregatedProduct: boolean
  onWishlistClick?: (ev: React.MouseEvent) => Promise<void> | void
  onClick?: (ev: React.MouseEvent) => void
  importance?: 'low' | 'high'
  alt?: string
}

interface Props extends ProductTileInfo {
  disableContent?: boolean
  className?: string
  tileRef?: React.RefObject<HTMLAnchorElement | HTMLElement>
}

const ProductTile: React.FC<Props> = ({
  name,
  productName,
  href,
  badgesList,
  colorsList,
  variantsList,
  primaryImageDefaultSource,
  primaryImageTypeTitle,
  secondaryImageDefaultSource,
  secondaryImageTypeTitle,
  primaryImageSrcSet,
  secondaryImageSrcSet,
  sizes,
  primaryPadded,
  secondaryPadded,
  formattedBasePrice,
  formattedSalePrice,
  formattedCampaignPrice,
  lowestPriorPrice,
  onClick,
  wishlisted,
  onWishlistClick,
  tileRef,
  disableContent = false,
  importance,
  alt,
  CTAContent,
  onCTAClick,
  hasCTA,
  enableProductName,
  enableSustainabilityIcons,
  sustainability,
  secondaryLabelBasePrice,
  secondaryLabelSalePrice,
  secondaryLabelCampaignPrice,
  isMarketplaceProduct,
  isSecondHandProduct,
  enablePriceDisplayOverhaul,
  isAggregatedProduct,
  ...props
}) => {
  const fallbackTileRef: Props['tileRef'] = React.useRef(null)
  tileRef = tileRef ?? fallbackTileRef
  return (
    <ProductTileSelector
      data-testid="ProductTile"
      as={href ? 'a' : 'section'}
      href={href}
      onClick={onClick}
      ref={tileRef}
      hasCTA={hasCTA}
      {...props}
    >
      {badgesList ? (
        <ProductImageWithBadges
          src={primaryImageDefaultSource}
          srcSet={primaryImageSrcSet}
          sizes={sizes}
          badgesList={badgesList}
          badgesPosition="bottom-start"
          padded={primaryPadded}
          reducedPaddingTop={!!secondaryLabelBasePrice}
          importance={importance}
          loading={importance === 'high' ? 'eager' : undefined}
          alt={`${alt}, ${primaryImageTypeTitle}`}
        />
      ) : (
        <ProductImage
          padded={primaryPadded}
          src={primaryImageDefaultSource}
          srcSet={primaryImageSrcSet}
          sizes={sizes}
          reducedPaddingTop={!!secondaryLabelBasePrice}
          alt={alt}
        />
      )}
      <ProductTileHover
        hoverRef={tileRef}
        src={secondaryImageDefaultSource}
        srcSet={secondaryImageSrcSet}
        sizes={sizes}
        padded={secondaryPadded}
        onWishlistClick={onWishlistClick}
        wishlisted={wishlisted}
        reducedPaddingTop={!!secondaryLabelBasePrice}
        alt={`${alt}, ${secondaryImageTypeTitle}`}
      />
      {!disableContent && (
        <ProductTileContent
          CTAContent={CTAContent}
          onCTAClick={onCTAClick}
          hasCTA={hasCTA}
          name={name}
          productName={productName}
          colorsList={colorsList}
          variantsList={variantsList}
          formattedBasePrice={formattedBasePrice}
          formattedSalePrice={formattedSalePrice}
          formattedCampaignPrice={formattedCampaignPrice}
          secondaryLabelBasePrice={secondaryLabelBasePrice}
          secondaryLabelSalePrice={secondaryLabelSalePrice}
          secondaryLabelCampaignPrice={secondaryLabelCampaignPrice}
          lowestPriorPrice={wishlisted ? undefined : lowestPriorPrice}
          enableProductName={enableProductName}
          isMarketplaceProduct={isMarketplaceProduct}
          isSecondHandProduct={isSecondHandProduct}
          enablePriceDisplayOverhaul={enablePriceDisplayOverhaul}
          isAggregatedProduct={isAggregatedProduct}
        />
      )}
    </ProductTileSelector>
  )
}

export default ProductTile
